import { useEffect, useState } from 'react';
import Select from 'react-select';
import { AppsFiltersStyles } from 'app/components/common/common-styles';
import { AUTH_TOKEN, statusCode } from 'app/constants';
import { SIGN_IN_PATH } from 'app/route/paths';
import { FilterAdapter, useUrlFilterController } from 'app/components/filters/url-filters';
// adjust import path to wherever you defined it
import { getAppCategories, AppCategory } from 'app/console-api/apps/categories';

type CategoryOption = {
  id: number;
  value: string; // category name (used in URL CSV)
  label: string;
};

export const CategoriesAdapter: FilterAdapter<string[]> = {
  key: 'categories',
  fromUrl: (sp) => {
    const raw = sp.get('categories');
    if (!raw) return null;
    const arr = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return arr.length ? arr : null;
  },
  toUrl: (sp, value) => {
    const next = new URLSearchParams(sp.toString());
    if (!value || value.length === 0) {
      next.delete('categories');
    } else {
      next.set('categories', value.join(','));
    }
    return next;
  },
  // stable cache key (sorted)
  toKey: (v) => `cats:${v && v.length ? [...v].sort().join(',') : 'all'}`
};

export function AppCategoriesFilter({
  appID,
  placeholder = 'All categories'
}: {
  appID: string;
  placeholder?: string;
}) {
  const ctl = useUrlFilterController();
  const value = ctl.get(CategoriesAdapter) ?? []; // string[] of selected category names
  const [options, setOptions] = useState<CategoryOption[]>([]);

  useEffect(() => {
    let mounted = true;
    getAppCategories(appID)
      .then((res: any) => {
        // Be tolerant to different shapes: items | data | array
        const list: AppCategory[] = res?.items ?? res?.data ?? res ?? [];
        const opts: CategoryOption[] = list.map((c) => ({
          id: c.id,
          value: c.name, // URL uses names per backend
          label: c.name
        }));
        if (mounted) setOptions(opts);
      })
      .catch((error: any) => {
        if (error?.statusCode === statusCode.authentication_error) {
          localStorage.removeItem(AUTH_TOKEN);
          window.location.href = SIGN_IN_PATH;
          return;
        }
        console.error('Failed to fetch app categories:', error);
      });
    return () => {
      mounted = false;
    };
  }, [appID]);

  // react-select multi expects an array of option objects
  const selected = options.filter((o) => value.includes(o.value));

  return (
    <Select<CategoryOption, true>
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      isSearchable={false}
      styles={AppsFiltersStyles()}
      options={options}
      value={selected}
      placeholder={placeholder}
      onChange={(opts) => {
        const names = (opts ?? []).map((o) => o.value);
        ctl.set(CategoriesAdapter, names.length ? names : null);
      }}
    />
  );
}
